import PropTypes from 'prop-types';
import { useEffect, useReducer, useMemo, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from './firebase';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIAL':
      return {
        loading: false,
        user: action.payload.user,
      };
    case 'LOGIN':
    case 'REGISTER':
      return {
        ...state,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
      };
    default:
      return state;
  }
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // INITIALIZE on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      dispatch({
        type: 'INITIAL',
        payload: { user },
      });
    });

    return () => unsubscribe();
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    const getDateString = (date) => date.toISOString().split('T')[0];

    const checkAndResetStreak = async (uid) => {
      if (!uid) return;

      const statsRef = doc(db, 'users', uid);

      try {
        const snap = await getDoc(statsRef);
        if (!snap.exists()) return;

        const stats = snap.data().stats || {};
        const lastWinDate = stats.lastWinDate;
        const today = getDateString(new Date());
        const yesterday = getDateString(new Date(Date.now() - 1000 * 60 * 60 * 24));

        if (lastWinDate !== today && lastWinDate !== yesterday) {
          // No win in the last 2 calendar days, reset streak
          await updateDoc(statsRef, {
            'stats.currentStreak': 0,
          });
        }
      } catch (err) {
        console.error('Failed to check/reset streak', err);
      }
    };

    checkAndResetStreak(userCredential.user.uid);

    dispatch({
      type: 'LOGIN',
      payload: { user: userCredential.user },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Optional: update displayName
    await updateProfile(userCredential.user, {
      displayName: `${firstName} ${lastName}`,
    });

    dispatch({
      type: 'REGISTER',
      payload: { user: { ...userCredential.user, displayName: `${firstName} ${lastName}` } },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(auth);
    dispatch({ type: 'LOGOUT' });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      register,
      logout,
    }),
    [login, register, logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

// import PropTypes from 'prop-types';
// import { useEffect, useReducer, useCallback, useMemo } from 'react';
// // utils
// import axios, { endpoints } from 'src/utils/axios';
// //
// import { AuthContext } from './auth-context';
// import { isValidToken, setSession } from './utils';

// // ----------------------------------------------------------------------

// // NOTE:
// // We only build demo at basic level.
// // Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// // ----------------------------------------------------------------------

// const initialState = {
//   user: null,
//   loading: true,
// };

// const reducer = (state, action) => {
//   if (action.type === 'INITIAL') {
//     return {
//       loading: false,
//       user: action.payload.user,
//     };
//   }
//   if (action.type === 'LOGIN') {
//     return {
//       ...state,
//       user: action.payload.user,
//     };
//   }
//   if (action.type === 'REGISTER') {
//     return {
//       ...state,
//       user: action.payload.user,
//     };
//   }
//   if (action.type === 'LOGOUT') {
//     return {
//       ...state,
//       user: null,
//     };
//   }
//   return state;
// };

// // ----------------------------------------------------------------------

// const STORAGE_KEY = 'accessToken';

// export function AuthProvider({ children }) {
//   const [state, dispatch] = useReducer(reducer, initialState);

//   const initialize = useCallback(async () => {
//     try {
//       const accessToken = sessionStorage.getItem(STORAGE_KEY);

//       if (accessToken && isValidToken(accessToken)) {
//         setSession(accessToken);

//         const response = await axios.get(endpoints.auth.me);

//         const { user } = response.data;

//         dispatch({
//           type: 'INITIAL',
//           payload: {
//             user,
//           },
//         });
//       } else {
//         dispatch({
//           type: 'INITIAL',
//           payload: {
//             user: null,
//           },
//         });
//       }
//     } catch (error) {
//       console.error(error);
//       dispatch({
//         type: 'INITIAL',
//         payload: {
//           user: null,
//         },
//       });
//     }
//   }, []);

//   useEffect(() => {
//     initialize();
//   }, [initialize]);

//   // LOGIN
//   const login = useCallback(async (email, password) => {
//     const data = {
//       email,
//       password,
//     };

//     const response = await axios.post(endpoints.auth.login, data);

//     const { accessToken, user } = response.data;

//     setSession(accessToken);

//     dispatch({
//       type: 'LOGIN',
//       payload: {
//         user,
//       },
//     });
//   }, []);

//   // REGISTER
//   const register = useCallback(async (email, password, firstName, lastName) => {
//     const data = {
//       email,
//       password,
//       firstName,
//       lastName,
//     };

//     const response = await axios.post(endpoints.auth.register, data);

//     const { accessToken, user } = response.data;

//     sessionStorage.setItem(STORAGE_KEY, accessToken);

//     dispatch({
//       type: 'REGISTER',
//       payload: {
//         user,
//       },
//     });
//   }, []);

//   // LOGOUT
//   const logout = useCallback(async () => {
//     setSession(null);
//     dispatch({
//       type: 'LOGOUT',
//     });
//   }, []);

//   // ----------------------------------------------------------------------

//   const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

//   const status = state.loading ? 'loading' : checkAuthenticated;

//   const memoizedValue = useMemo(
//     () => ({
//       user: state.user,
//       method: 'jwt',
//       loading: status === 'loading',
//       authenticated: status === 'authenticated',
//       unauthenticated: status === 'unauthenticated',
//       //
//       login,
//       register,
//       logout,
//     }),
//     [login, logout, register, state.user, status]
//   );

//   return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
// }

// AuthProvider.propTypes = {
//   children: PropTypes.node,
// };
